import { Button, Checkbox, DatePicker, message, Row, Table, Typography, Card } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { serverFetch } from "../../server";
import { fmtDate, strDate2Server } from "../../extensions/utils";
import { renderCheckbox, storeDocVidName, storeDocVids } from "../../extensions/helper";
import StockDoc from "./stock-doc";
import { AddFilter, FilterItem, SportTooltip } from "../../controls";
import StockOrder from "./stock-order";
import ButtonReport from "../../controls/button-report/button-report";
import TriStateCheckbox from "../../controls/tri-state-checkbox";
import { HelpButton } from "../../extensions/design";
import { isMobile, menuSize } from "../../extensions/design";
const { Text } = Typography;

const StockDocs = ({ filter: externalFilter, onItemsChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [filter, setFilter] = useState(() => ({
    dateFrom: dayjs().subtract(1, "month"),
    dateTo: dayjs(),
    Active: null,
  }));

  useEffect(() => {
    const nextFilter = {...filter};
    getData(nextFilter, "period-change");
  }, [filter.dateFrom, filter.dateTo]);
  
  useEffect(() => {
    const rawFilter = externalFilter || {};
    const simplifiedFilter = {};

    Object.keys(rawFilter).forEach(key => {
      const item = rawFilter[key];
      if (item && typeof item === 'object' && 'value' in item) {
        simplifiedFilter[key] = item.value;
      } else {
        simplifiedFilter[key] = item;
      }
    });

    const nextFilter = { ...filter, ...simplifiedFilter };
    setFilter(nextFilter);
    getData(nextFilter);
  }, [externalFilter]);
  

  const divRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [newOrder, setNewOrder] = useState(false);
  const [addingFilter, setAddingFilter] = useState(true);

  const getUniqueClientOptions = (idField, nameField) => {
    const map = new Map();
    items.forEach(item => {
      const id = item[idField];
      const name = item[nameField];
      if (id && name) {
        map.set(id, name);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  };
  
  const filterOptions = [
    { type: "clientOutIds", label: "Отправитель", tooltip: "Отправитель", inputType: "multiselect", options: getUniqueClientOptions("clientOutId", "clientOutName")},/*  */
    { type: "clientInIds", label: "Получатель", tooltip: "Получатель", inputType: "multiselect", options: getUniqueClientOptions("clientInId", "clientInName")},/*  */
    { type: "documentVidIds", label: "Вид документа", tooltip: "По виду документа", inputType: "multiselect", options: storeDocVids, width: 250 },/*  */
  ];

  const filterLabels = {
    clientOutIds: "Отправитель",
    clientInIds: "Получатель",
    documentVidIds: "Вид документа",
  };

  const renderFilterItem = (key, data) => {
    const option = filterOptions.find(f => f.type === key);
    return (
      <FilterItem key={key} type={option?.inputType} label={filterLabels[key] || key} value={data.value} options={option?.options}
        onChange={(val) => onFilterChange({ [key]: val })}
        onRemove={() => {
          const { [key]: _, ...rest } = filter;
          setFilter(rest);
        }}
      />
    );
  };
  const filters = Object.entries(filter)
    .filter(([key, data]) => !["dateFrom", "dateTo", "Active"].includes(key))
    .map(([type, data]) => ({ type, ...data }));

  const buildParams = useCallback((filter) => {
    const params = new URLSearchParams();
    /* params.append("Offset", offset);
    params.append("Limit", PAGE_SIZE); */
    /* if (searchValue) params.append("SearchText", searchValue); */
    if (filter.clientOutIds?.length) filter.clientOutIds.forEach(v => params.append("clientOutIds", v));
    if (filter.clientInIds?.length) filter.clientInIds.forEach(v => params.append("clientInIds", v));
    if (filter.documentVidIds?.length) filter.documentVidIds.forEach(v => params.append("documentVidIds", v));
    if (filter.Active != null) params.append("Active", filter.Active);
    
    return params;

  }, [filter]);

  const getData = (f = filter) => {
    setLoading(true);
    serverFetch(`store?dateFrom=${strDate2Server(f.dateFrom)}&dateTo=${strDate2Server(f.dateTo)}&${buildParams(f)}`)
      .then(data => {
        setLoading(false);
        setItems(data);
        onItemsChange?.(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(getData, []);
  
  const handleAddTopFilter = (type) => {
    if (filter[type]) return;

    const option = filterOptions.find(opt => opt.type === type);
    const inputType = option?.inputType;
    let initialValue = undefined;
    let options = option?.options;
    onFilterChange({ [type]: { value: initialValue, inputType, options }});
    setAddingFilter(false);
  };

  const onFilterChange = (changedValues) => {
    const newFilter = { ...filter, ...changedValues };
    setFilter(newFilter);
    if (isMobile) {
      getData(newFilter);
    }
  };

  const columns = [
    { dataIndex: "id",title: "#", },
    { dataIndex: "docDate", render: fmtDate, title: "Дата", },
    { dataIndex: "active", render: renderCheckbox, title: "Акт.", },
    { dataIndex: "vid", title: "Вид", render: storeDocVidName },
    { dataIndex: "clientOutName", title: "Отправитель", },
    { dataIndex: "clientInName", title: "Получатель", },
    { dataIndex: "comment", title: "Комментарий", },
    { dataIndex: "syma",title: "Сумма", align: "right"},
  ]

  const headerSize =  divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onAdd = () => {
    setSelected(0);
  }

  const onClose = refresh => {
    setSelected(null);
    refresh && getData(filter);
  }

  const onDocDataChanged = () => {
    getData(filter);
  }

  const onCloseOrder = refresh => {
    setNewOrder(false);
    refresh && getData(filter);
  }

  return !isMobile ? (<div>
    <div className="sport-form-title">
        <div>Период с</div>
        <DatePicker value={filter.dateFrom} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateFrom: e })} allowClear={false}/>
        <div>по</div>
        <DatePicker value={filter.dateTo} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateTo: e })} allowClear={false}/>
        <Button onClick={() => getData(filter)}>Обновить</Button>
        <Button onClick={onAdd}>Новый</Button>
        <SportTooltip title="Сформировать заказ поставщику для товаров с недостаточными остатками">
          <Button onClick={() => setNewOrder(true)}>Заказ поставщику</Button>
        </SportTooltip>
        <ButtonReport id={selected} vid={5} begPer={filter.dateFrom} endPer={filter.dateTo} />
        <TriStateCheckbox name="Active" value={filter.Active} onChange={(val) => onFilterChange({ Active: val })}>Активный</TriStateCheckbox>
        <HelpButton article="uchet-nakladnyh"/>
      </div>
    <div ref={divRef} style={{width: tableWidth}}>
      <Row style={{ marginLeft: "8px", marginBottom:"8px" }}>
        <div className="filter-container">
          {filters.length === 0 && !addingFilter && (
            <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
          )}
          {filters.map((f) => renderFilterItem(f.type, f))}
          {filters.length < filterOptions.length && (
            addingFilter ? (
              <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
            ) : (
              filters.length !== 0 && (
                <Button onClick={() => setAddingFilter(true)} type="dashed" className="filter-add-button">+</Button>
              )
            )
          )}
        </div>
      </Row>  
    <Table dataSource={items} loading={loading} columns={columns} pagination={false} rowKey="id" size="small" scroll={{ y: tableHeight }}
      rowSelection={{ type: 'radio', selectedRowKeys: [selected], renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
      onRow={(record) => ({onClick: () => {setSelected(record.id)}})}
    />
    </div>
    {selected !== null && <StockDoc id={selected} onClose={onClose} onDocDataChanged={onDocDataChanged} />}
    {newOrder && <StockOrder onClose={onCloseOrder}/>}
  </div>) : (
    <>
      <div style={{display:"flex", justifyContent: "space-around", alignItems:"center"}}>
        <Text>Период с </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateFrom} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateFrom: e })} allowClear={false}/>
        <Text> по </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateTo} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateTo: e })} allowClear={false}/>
        <ButtonReport id={selected} vid={5} begPer={filter.dateFrom} endPer={filter.dateTo} style={{ fontSize: 28 }} />
      </div>
      <div className="client-item-line-double-halfed" style={{margin: "4px"}}>
        <Button onClick={onAdd}>Новый</Button>
        <Button onClick={() => setNewOrder(true)}>Заказ поставщику</Button>
      </div>
      <div className="stock-scrollable-list" style={{ height: window.innerHeight - menuSize - 100}}>
        {items.map(item => {
          return (
          <Card key={item.id} className="stock-card" style={{ borderLeft: `4px solid ${item?.active ? "#2f6f00" : "#d9d9d9"}` }} onClick={() => {setSelected(item.id)}}>
            <div className="stock-osv-item-line-triple-non-even">
              <Text strong> {fmtDate(item.docDate)} </Text>
              <Text> {storeDocVidName(item.vid)} </Text>
              <Text style={{textAlign: "right"}}strong> #{item.id} </Text>
            </div>
            <div className="stock-osv-item-line-long-and-short">
              <Text className="sender"> Отправ.: {item.clientOutName} </Text>
              <Text style={{textAlign: "right"}}> Сумма: {item.syma} </Text>
            </div>
            <div className="stock-osv-item-line-long-and-short">
              <Text className="sender" style={{textAlign: "left"}}> Получ.: {item.clientInName} </Text>
              <div style={{textAlign: "right", fontSize: "12px"}}>
                <TriStateCheckbox disabled={true} name="Active" value={item.active} onChange={() => {}}>
                  <label style={{fontSize: "12px"}}>Активный</label>
                </TriStateCheckbox>
              </div>
            </div>
            <div>
              <Text>Комм.: {item.comment}</Text>
            </div>
          </Card>
        )})}
        {(items.length === 0 && !loading) ?
        <div style={{ textAlign: "center", marginTop: "20px", fontSize:"14px" }}>Нет данных
        </div> : <></>}
      </div>
      <></>
      {newOrder && <StockOrder onClose={onCloseOrder}/>}
      {selected !== null && <StockDoc id={selected} onClose={onClose} onDocDataChanged={onDocDataChanged} />}
    </>
  )
}

export default StockDocs;